import * as Yup from "yup";

import { ALLOWANCE, CHARGE } from "../constants/defaults";
import { createAllowanceChargeSchema } from "./allowanceChargeValidation";
import {
    isAnyProfitMarginTransactionType,
    isProfitMarginSelfBillingTransactionType
} from "../resources/common/isProfitMarginTransactionType";
import {isSimplifiedTransactionType} from "../resources/common/isSimplifiedTransactionType";
import {isImportOfGoodsTransactionType} from "../resources/common/isImportOfGoodsTransactionType";
import {isImportOfRCMTransactionType} from "../resources/common/isImportofRCMTransactionType";

const PROFIT_MARGIN_HS_BANNED_PREFIXES = [
  "7101",
  "7102",
  "7103",
  "7104",
  "01",
  "06",
];
const PROFIT_MARGIN_HS_PREFIX_MESSAGE =
  "HS classification code is not allowed for profit margin invoices when the code starts with 7101, 7102, 7103, 7104, 01 or 06";
const OMAN_CLASSIFICATION_LIST_IDS = ["HS", "MP"];
const OMAN_CLASSIFICATION_LIST_ID_MESSAGE =
  "Item classification list id must be HS or MP";
const GOODS_CLASSIFICATION_LIST_ID_MESSAGE =
  "Item classification list id must be HS when item type is G";
const SERVICES_CLASSIFICATION_LIST_ID_MESSAGE =
  "Item classification list id must be MP when item type is S";

const listIdMatchesItemType = (listId, itemType) => {
  if (itemType === "G" && listId !== "HS") {
    return GOODS_CLASSIFICATION_LIST_ID_MESSAGE;
  }

  if (itemType === "S" && listId !== "MP") {
    return SERVICES_CLASSIFICATION_LIST_ID_MESSAGE;
  }

  return null;
};

const itemTypeSchemeTest = function (value) {
  const itemType =
    this.from?.[1]?.value?.item_specification_document_reference?.item_type;
  const mismatch = listIdMatchesItemType(value, itemType);

  if (mismatch) {
    return this.createError({ message: mismatch });
  }

  return true;
};

const getAllowanceChargeMessages = (messages = {}) =>
  messages?.allowance_charge || {};

export const getLineItemValidationSchema = (
  messages = {},
  transactionType = null,
) => {
  const m = messages?.line_item || {};
  const allowanceChargeMessages = getAllowanceChargeMessages(messages);

  const allowanceValidationSchema = createAllowanceChargeSchema(
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER,
    ALLOWANCE,
    false,
    false,
    false,
    allowanceChargeMessages,
  );

  const chargeValidationSchema = createAllowanceChargeSchema(
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER,
    CHARGE,
    false,
    false,
    false,
    allowanceChargeMessages,
  );

  return Yup.object().shape({
    name: Yup.string()
      .min(1, "Minimum 1 character")
      .max(100, "Maximum 100 characters")
      .required("Name is required"),
    description: Yup.string().required("Description is required"),
    unit_code: Yup.mixed().nullable().required("Unit code is required"),
    quantity: Yup.number()
      .transform((value, originalValue) => {
        return !originalValue ? undefined : value;
      })
      .required("Quantity is required")
      .min(1, "Minimum quantity is 1"),
    additional_item_identification: Yup.object().shape({
      nature_code: Yup.string().when(
          "transaction_type",
          {
              is: _ => !isSimplifiedTransactionType(transactionType) && !isProfitMarginSelfBillingTransactionType(transactionType) &&
                  !isImportOfGoodsTransactionType(transactionType) && !isImportOfRCMTransactionType(transactionType),
              then: schema => schema.required("Nature code is required").length(6, "Nature code should be of 6 digits"),
              otherwise: schema => schema.nullable(),
          }
      )
    }),
    additional_item_property: Yup.object().shape({
        attribute_name: Yup.string().nullable().test(
            "item-attribute-name-required",
            "Item Attribute is required",
            function (value) {
                const attributeName = this.parent?.attribute_name;
                return !(isAnyProfitMarginTransactionType(transactionType) && !attributeName);
            }
        ),
        attribute_value: Yup.string().nullable().test(
            "item-attribute-value-required",
            "Item Attribute value is required",
            function (value) {
                const attributeValue = this.parent?.attribute_value;
                return !(isAnyProfitMarginTransactionType(transactionType) && !attributeValue);
            }
        ),
          name_code: Yup.string().nullable().test(
              "profit-margin-type-code-required",
              "Profit Margin type code is required for Profit Margin/Self-billed Profit margin documents.",
              function (value) {
                  const nameCode = this.parent?.name_code;
                  return !(isAnyProfitMarginTransactionType(transactionType) && !nameCode);
              }
          )
    }),
      item_specification_document_reference: Yup.object().shape({
          commodity_code: Yup.string().when(
              "transaction_type",
              {
                  is: _ => !isSimplifiedTransactionType(transactionType),
                  then: schema => schema.required("Commodity code is required")
                      .max(8, "Maximum 8 characters"),
                  otherwise: schema => schema.nullable(),
              }
          )
          ,
          item_type: Yup.string().when(
              "transaction_type",
              {
                  is: _ => !isSimplifiedTransactionType(transactionType),
                  then: schema => schema.required("Item type is required"),
                  otherwise: schema => schema.nullable(),
              }
          ),
      }),
    commodity_classification: Yup.object().shape({
      item_classification_code: Yup.string().when(
          'item_classification_list_id',
          {
              is: listId => !isSimplifiedTransactionType(transactionType) && listId === "HS",
              then: schema => schema.required("Commodity classification code is required for Goods"),
              otherwise: schema => schema.nullable(),
          }
      ).test(
          "profit-margin-hs-prefix",
          PROFIT_MARGIN_HS_PREFIX_MESSAGE,
          function (value) {
            const listId = this.parent?.item_classification_list_id;
            if (
              !isAnyProfitMarginTransactionType(transactionType) ||
                isSimplifiedTransactionType(transactionType) ||
              listId !== "HS" ||
              !value
            ) {
              return true;
            }

            return !PROFIT_MARGIN_HS_BANNED_PREFIXES.some((prefix) =>
              String(value).trim().startsWith(prefix),
            );
          },
        ),
      item_classification_list_id: Yup.string()
          .when('transaction_type',
              {
              is: _ => !isSimplifiedTransactionType(transactionType),
              then: schema => schema.required("Item classification list ID is required").oneOf(
                      OMAN_CLASSIFICATION_LIST_IDS,
                      OMAN_CLASSIFICATION_LIST_ID_MESSAGE,
                  ),
              otherwise: schema => schema.nullable(),
          })
        .test(
          "item-type-scheme",
          SERVICES_CLASSIFICATION_LIST_ID_MESSAGE,
          itemTypeSchemeTest,
        ),
    }),
    classified_tax_category: Yup.object().shape({
      id: Yup.string().nullable().required("Tax category is required"),
      tax_exemption_reason_code: Yup.string()
        .nullable()
        .test(
          "tax-exemption-reason-required",
          "Tax exemption reason code is required for E tax category",
          function (value) {
            const { id } = this.parent;
            if (id === "E") {
              return value !== null && value !== undefined && value !== "";
            }
            return true;
          },
        ),
      tax_exemption_reason: Yup.string().nullable(),
      zero_rated_reason_code: Yup.string()
        .nullable()
        .test(
          "zero-rated-reason-code-required",
          "Zero rated reason code is required for Z tax category",
          function (value) {
            const { id } = this.parent;
            if (id === "Z") {
              return value !== null && value !== undefined && value !== "";
            }
            return true;
          },
        ),
      zero_rated_reason: Yup.string()
        .nullable()
        .test(
          "zero-rated-reason-required",
          "Zero rated reason is required for Z tax category",
          function (value) {
            const { id } = this.parent;
            if (id === "Z") {
              return value !== null && value !== undefined && value !== "";
            }
            return true;
          },
        ),
    }),

    allowances: Yup.array().of(allowanceValidationSchema),
    charges: Yup.array().of(chargeValidationSchema),
  });
};

export const getProductValidationSchema = () => {
  return Yup.object().shape({
    name: Yup.string()
      .min(1, "Minimum 1 character")
      .max(100, "Maximum 100 characters")
      .required("Name is required"),
    description: Yup.string().max(300, "Maximum 300 characters").nullable(),
    unit_of_measure: Yup.mixed()
      .nullable()
      .required("Unit of measure is required"),
    quantity: Yup.number()
      .transform((value, originalValue) => {
        return !originalValue ? undefined : value;
      })
      .required("Quantity is required")
      .min(1, "Minimum quantity is 1"),
    unit_price: Yup.number()
      .transform((value, originalValue) => {
        return originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
          ? undefined
          : value;
      })
      .required("Unit price is required")
      .min(0, "Minimum unit price is 0"),
    additional_item_identification: Yup.object().shape({
      nature_code: Yup.string()
        .required("Nature code is required")
        .max(6, "Maximum 6 characters"),
    }),
    item_specification_document_reference: Yup.object().shape({
      commodity_code: Yup.string()
        .required("Commodity code is required")
        .max(8, "Maximum 8 characters"),
      item_type: Yup.string().required("Item type is required"),
    }),
    commodity_classification: Yup.object().shape({
      item_classification_code: Yup.string().nullable(),
      item_classification_list_id: Yup.string()
        .required("Item classification list ID is required")
        .oneOf(
          OMAN_CLASSIFICATION_LIST_IDS,
          OMAN_CLASSIFICATION_LIST_ID_MESSAGE,
        )
        .test(
          "item-type-scheme",
          SERVICES_CLASSIFICATION_LIST_ID_MESSAGE,
          itemTypeSchemeTest,
        ),
    }),
    classified_tax_category: Yup.object().shape({
      id: Yup.string().nullable().required("Tax category is required"),
      percent: Yup.number()
        .min(0, "Tax rate must be at least 0")
        .max(100, "Tax rate must be at most 100"),
      tax_exemption_reason_code: Yup.string()
        .nullable()
        .test(
          "tax-exemption-reason-required",
          "Tax exemption reason code is required for E tax category",
          function (value) {
            const { id } = this.parent;
            if (id === "E") {
              return value !== null && value !== undefined && value !== "";
            }
            return true;
          },
        ),
      tax_exemption_reason: Yup.string().nullable(),
      zero_rated_reason_code: Yup.string()
        .nullable()
        .test(
          "zero-rated-reason-code-required",
          "Zero rated reason code is required for Z tax category",
          function (value) {
            const { id } = this.parent;
            if (id === "Z") {
              return value !== null && value !== undefined && value !== "";
            }
            return true;
          },
        ),
      zero_rated_reason: Yup.string()
        .nullable()
        .test(
          "zero-rated-reason-required",
          "Zero rated reason is required for Z tax category",
          function (value) {
            const { id } = this.parent;
            if (id === "Z") {
              return value !== null && value !== undefined && value !== "";
            }
            return true;
          },
        ),
    }),
  });
};
